import { ReactElement, useState } from "react";
import classNames from "classnames";
import { PersonAvatarStatus, PersonAvatarStatusKind } from "./PersonAvatarStatus";
import styles from "./PersonAvatar.module.less";
import {
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { Icon, SvgIconValue } from "../Icon/Icon";
import { PersonAvatarIcon } from "./PersonAvatarIcon";
import { never } from "client_legacy/ndaw/errors";
import { useEffectSkipFirstRender } from "client/Hooks";

export type PersonAvatarSize =
	| "x-small"
	| "small"
	| "default"
	| "large"
	| "x-large"
	| "xx-large"
	| "xxx-large";

enum LoadResult {
	Success = 0,
	Failed = 1
}

type PersonAvatarState = {
	loadResult: LoadResult | null;
};

const sizeToStyle: { [key in PersonAvatarSize]: string | null } = {
	"x-small": styles.root_xSmall,
	"small": styles.root_small,
	"default": null,
	"large": styles.root_large,
	"x-large": styles.root_xLarge,
	"xx-large": styles.root_xxLarge,
	"xxx-large": styles.root_xxxLarge
};

export type PersonAvatarStatusValue = {
	kind: "status";
	status: PersonAvatarStatusKind | undefined;
} | {
	kind: "icon";
	iconTitle?: string;
	icon: SvgIconValue;
};

export type PersonAvatarProps = {
	imageSrc?: string | nil;
	size?: PersonAvatarSize;
	status?: PersonAvatarStatusValue;
	trackingId?: string;
} & ({
	placeholderKind?: "initials";
	initials: string;
} | {
	placeholderKind: "user-icon";
	initials?: undefined;
});

export const PersonAvatar = (props: PersonAvatarProps): ReactElement => {
	const {
		imageSrc,
		placeholderKind = "initials",
		initials,
		size = "default",
		status,
		trackingId
	} = props;

	const [state, setState] = useState<PersonAvatarState>({ loadResult: null });

	useEffectSkipFirstRender(
		() => {
			hasImplicitDependencyOn(imageSrc);
			setState({ loadResult: null });
		},
		[imageSrc]
	);

	const { loadResult } = state;

	const showInitials: boolean = loadResult === null || loadResult === LoadResult.Failed;

	const renderStatus = (): ReactElement | null => {
		if (!status) {
			return null;
		}

		switch(status.kind) {
			case "icon":
				return (
					<div className={styles.status}>
						<PersonAvatarIcon
							icon={status.icon}
							title={status.iconTitle}
							size={size}
						/>
					</div>
				);
			case "status":
				return isNotNil(status.status)
					? (
						<div className={styles.status}>
							<PersonAvatarStatus kind={status.status} size={size} />
						</div>
					)
					: null;
			default:
				never(status);
		}
	};

	const renderPlaceholder = (): ReactElement => {
		switch (placeholderKind) {
			case "initials":
				return (
					<div className={classNames(styles.noPhoto, styles.noPhoto_initials)}>
						{initials}
					</div>
				);
			case "user-icon":
				return (
					<div className={classNames(styles.noPhoto, styles.noPhoto_icon)}>
						<Icon value="user" />
					</div>
				);
			default:
				never(placeholderKind);
		}
	};

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(styles.root, sizeToStyle[size])}
		>
			<div className={styles.circle}>
				{ imageSrc && (
					<img
						className={styles.photo}
						src={imageSrc}
						style={{display: loadResult === LoadResult.Failed ? "none": undefined}}
						onLoad={() => setState(x => ({ ...x, loadResult: LoadResult.Success}))}
						onError={() => setState(x => ({ ...x, loadResult: LoadResult.Failed}))}
					/>
				)}
				{showInitials && (
					renderPlaceholder()
				)}
			</div>
			{renderStatus()}
		</div>
	);
};

PersonAvatar.displayName = "PersonAvatar";
